<template>
  <div
    v-hotkey="shortcuts"
    v-click-outside="unselect"
    class="event"
    :class="{
      [eventColorStyle]: true,
      short: duration < 35,
      task: task !== null,
      past,
      meeting: attendees > 0,
      multiple: attendees > 2,
      draggingOver,
      ongoing,
      compact,
      hasChildren: children && children.length > 0,
      isSelected,
      small,
    }"
    :style="[style]"
    @click.prevent="select"
    @dblclick="openEvent($event)"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <div class="line">
      <div v-if="draggedEvent" class="dragged-start">
        {{ draggedEventTime }}
      </div>
      <div class="calendar-border" />
      <div class="content-line">
        <div class="left-details">
          <BaseCheckbox
            v-if="task"
            :is-checked="isTaskCompleted"
            :is-disabled="isBrokenIntegration"
            :checkbox-tooltip="taskCheckboxTooltip(isTaskCompleted, false)"
            @click="onCheckboxClick"
          />

          <WarningIcon
            v-if="isDeclinedByEverybodyElse"
            :class="['warning-icon']"
          />
          <span class="event-title" :class="{ checked: isTaskCompleted }">
            {{ event.title }}
          </span>
          <NotesIcon v-if="hasDescription" class="description" />
          <BaseButton
            v-if="parentTitle"
            class="hierarchy"
            kind="auxiliary"
            :text="parentTitle"
            :tooltip="parentTitle"
            tooltip-display="ellipsis"
            @click.stop="clickParent($event)"
          />
          <div v-if="eventLabels.length > 0" class="event-tags">
            <RTag
              v-for="label in eventLabels"
              :key="label.title"
              :text="label.title"
              :theme="label.theme"
              skin="dot"
              :has-padding="false"
              compact
            />
          </div>
          <ContextButton
            v-if="task"
            :task="task"
            :task-context="task.context"
            :integration-status="task.integrationStatus"
            size="md"
          />
          <BaseConferenceButton
            v-if="meeting && eventEndsInTheFuture"
            :meeting-name="meeting.name"
            @open-url="openUrl()"
          />
          <span
            v-if="isRecording"
            class="recording-indicator"
            aria-label="Recording in progress"
          />
        </div>
        <div class="right-details">
          <BaseCompactAttendees
            class="compact-attendees"
            :attendees="event.attendees"
            :past="past"
          />
          <span class="event-duration">
            {{ displayedDuration }}
          </span>
        </div>
      </div>
    </div>
    <div v-if="displayLocationLine" class="line second">
      <BaseLocation
        :location="event.location || undefined"
        :compact="compact"
      />
    </div>
    <ul
      v-if="children && children.length > 0"
      class="children"
      aria-label="Children"
    >
      <li v-for="child in children" :key="child.key" class="child">
        <PreviewedObject :target="child" />
      </li>
    </ul>
    <ProgressTime class="progress-time" :event="event" :compact="compact" />
  </div>
</template>

<script lang="ts">
import { Duration, intervalToDuration } from "date-fns";
import { computed, defineComponent, PropType, toRef, toRefs } from "vue";

import type { FullEvent, FullTask } from "@ctrl/controller-rpc";

import { eventActions } from "@/components/actions/actions";
import type { TemporaryEvent } from "@/components/agenda/dnd";
import { useHeightOfDuration } from "@/components/agenda/pixels";
import ProgressTime from "@/components/agenda/ProgressTime.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import BaseCompactAttendees from "@/components/base/BaseCompactAttendees.vue";
import BaseConferenceButton from "@/components/base/BaseConferenceButton.vue";
import BaseLocation from "@/components/base/BaseLocation.vue";
import { formatCompactDuration, getDaytime } from "@/components/dates";
import ContextButton from "@/components/elements/ContextButton.vue";
import RTag from "@/components/elements/RTag.vue";
import { fetchExtensionRow } from "@/components/graph/query";
import { asGraphRecordResult } from "@/components/graph/result";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import WarningIcon from "@/components/icons/WarningIcon.vue";
import * as inject from "@/components/inject";
import { labelsAsTags } from "@/components/labels";
import { getEventShortcuts } from "@/components/object-shortcuts";
import PreviewedObject, {
  type PreviewedObjectTarget,
} from "@/components/PreviewedObject.vue";
import {
  assignShortcuts,
  Shortcuts,
  unSelectItem,
} from "@/components/shortcuts";
import { EVENTS_TABLE_ID } from "@/components/tables";
import { taskCheckboxTooltip } from "@/components/task/checkbox-tooltip";
import { clickHierarchy } from "@/components/utils";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { conference, eventColor, isCancellable, totalAttendees } from "@/event";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { useParent } from "@/task";
import { extractChildren, type ChildrenSource } from "@/utils/hierarchy";
import { navigateToEvent } from "@/utils/navigate";
import { extractInlineReferences } from "@/utils/notes";

import { useEventInfo } from "../eventInfo";

type EventChildTarget = PreviewedObjectTarget & { key: string };

const CHILD_ROW_HEIGHT = 18;
// Space outside the child rows: card padding, title row and flex gaps. The
// details variant also reserves the context/location row below the children.
const EVENT_CHROME_HEIGHT = 28;
const EVENT_CHROME_WITH_DETAILS_HEIGHT = 57;

export default defineComponent({
  components: {
    NotesIcon,
    RTag,
    WarningIcon,
    BaseConferenceButton,
    BaseButton,
    BaseCheckbox,
    BaseCompactAttendees,
    BaseLocation,
    ContextButton,
    PreviewedObject,
    ProgressTime,
  },
  props: {
    base: { type: Number, required: true },
    draggedEvent: {
      type: Object as PropType<TemporaryEvent>,
    },
    draggingOver: { type: Boolean, required: true },
    event: { type: Object as PropType<FullEvent>, required: true },
    isRecording: { type: Boolean, required: false, default: false },
    isSelected: { type: Boolean, required: true },
    resizableDuration: { type: Object as PropType<Duration> },
    // The event's task, already resolved by the parent (EventBlock fetches
    // it for its drag-and-drop logic): `null` for a plain event. Left unset
    // only when mounted standalone, where the component fetches it itself.
    // ("given" because setup returns the resolved value as `task`.)
    givenTask: {
      type: Object as PropType<FullTask | null>,
      required: false,
      default: undefined,
    },
  },
  emits: {
    select: (_id: string) => true,
    unselect: () => true,
  },
  async setup(props, { emit }) {
    const { base, draggedEvent, event, resizableDuration, isSelected } =
      toRefs(props);
    const clock = inject.clock();
    const controller = inject.controller();
    const baseLogger = inject.logger();
    const calendars = inject.calendars();
    const locale = inject.locale();
    const router = inject.router();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const simpleModal = inject.simpleModal();
    const multipleEventsModal = inject.multipleEventsModal();
    const contextMenu = inject.contextMenu();
    const menu = inject.menu();
    const palette = inject.palette();
    const profile = inject.profile();
    const browserStore = useBrowserStore(profile.value.user.accountId);
    const theme = inject.theme();
    const workspaceId = computed(() => profile.value.user.personal.workspaceId);
    const releaser = useReleaser();
    // Prefer the parent-resolved task prop (`undefined` = not provided);
    // fetch only when mounted without it.
    const providedTask = toRef(props, "givenTask");
    const taskPromise =
      providedTask.value !== undefined
        ? Promise.resolve(computed(() => providedTask.value ?? null))
        : releaser.add(
            bindAsync(event, async ({ task }) =>
              task ? await controller.task.get(task) : releasableRef(null)
            )
          );
    const parentTitlePromise = releaser.add(
      taskPromise.then((task) =>
        useParent(controller, task).then(({ parentTitle }) => parentTitle)
      )
    );
    const eventGraphDataPromise = releaser.add(
      bindAsync(event, (ev) =>
        fetchExtensionRow(
          controller,
          workspaceId.value,
          EVENTS_TABLE_ID,
          ev?.extension_row
        )
      )
    );
    const eventInfo = useEventInfo(controller, calendars, event);
    releaser.add(
      eventInfo.then((eventInfo) => ({
        release: () => eventInfo.releaseEventInfo(),
      }))
    );
    const [{ calendar, isWritable }, eventGraphData, parentTitle, task] =
      await Promise.all([
        eventInfo,
        eventGraphDataPromise,
        parentTitlePromise,
        taskPromise,
      ]);
    const { height, ongoing, style } = useHeightOfDuration(
      event,
      clock,
      base.value
    );
    const duration = computed<number>(
      () => (+event.value.end - +event.value.start) / 1000 / 60
    );
    const eventColorStyle = computed(() => eventColor(calendar, theme));
    const small = computed(() => duration.value <= 120);
    // Remove special case for 1 hour when we do the notes display in here.
    const compact = computed<boolean>(
      () =>
        duration.value <= 15 ||
        (event.value.location &&
          duration.value >= 45 &&
          duration.value <= 75) ||
        false
    );
    const displayLocationLine = computed(
      () => !!event.value.location && duration.value >= 45
    );
    const notes = computed(() =>
      task.value ? task.value.notes : event.value.notes
    );
    const childrenSource = computed<ChildrenSource>(() => ({
      kind: "default",
      notes: notes.value,
    }));
    const childCapacity = computed(() => {
      const reservedHeight = displayLocationLine.value
        ? EVENT_CHROME_WITH_DETAILS_HEIGHT
        : EVENT_CHROME_HEIGHT;
      return Math.max(
        0,
        Math.floor((height.value - reservedHeight) / CHILD_ROW_HEIGHT)
      );
    });
    // The objects the notes merely REFERENCE (inline `#…` tokens), as card
    // targets badged with the reference arrow. An async binding rather than
    // part of the `visibleChildTargets` computed below because decoding an
    // object reference id into its workspace/table/row triple goes through
    // the controller (`graph.object.id.parse`).
    const referenceTargets = await releaser.add(
      bindAsync(notes, async (notes) => {
        const targets: EventChildTarget[] = [];
        for (const reference of extractInlineReferences(
          notes?.blocks ?? [],
          baseLogger
        )) {
          if (reference.kind === "task") {
            targets.push({
              kind: "task",
              id: reference.id,
              reference: true,
              key: `task|${reference.id}`,
            });
          } else if (reference.kind === "object") {
            const raw = await controller.graph.object.id.parse(reference.id);
            const resolvedWorkspaceId = raw.workspace_id ?? workspaceId.value;
            if (!resolvedWorkspaceId) continue;
            targets.push({
              kind: "object",
              target: {
                workspace_id: resolvedWorkspaceId,
                table_id: raw.table_id,
                row_id: raw.row_id,
              },
              reference: true,
              key: `object|${resolvedWorkspaceId}|${raw.table_id}|${raw.row_id}`,
            });
          } else {
            targets.push({
              kind: "reference",
              target: reference,
              reference: true,
              key: `reference|${reference.kind}|${reference.id}`,
            });
          }
        }
        return releasableRef(targets);
      })
    );
    const visibleChildTargets = computed<EventChildTarget[]>(() => {
      const capacity = childCapacity.value;
      if (capacity === 0) return [];

      const seen = new Set<string>();
      const targets: EventChildTarget[] = [];
      const fallbackWorkspaceId = workspaceId.value;
      // Push `target` unless its key is already listed; false once full.
      const push = (target: EventChildTarget): boolean => {
        if (!seen.has(target.key)) {
          seen.add(target.key);
          targets.push(target);
        }
        return targets.length < capacity;
      };
      // The children owned by the notes' blocks (todo and Object blocks).
      for (const child of extractChildren(
        childrenSource.value,
        ["object", "task"],
        baseLogger
      )) {
        let target: EventChildTarget;
        if (child.kind === "task") {
          target = { ...child, key: `task|${child.id}` };
        } else {
          if (!fallbackWorkspaceId) continue;
          const resolved = {
            workspace_id: child.target.workspace_id ?? fallbackWorkspaceId,
            table_id: child.target.table_id,
            row_id: child.target.row_id,
          };
          target = {
            key: `object|${resolved.workspace_id}|${resolved.table_id}|${resolved.row_id}`,
            kind: "object",
            target: resolved,
          };
        }
        if (!push(target)) return targets;
      }
      // Then the referenced targets (resolved above). Tasks and custom
      // objects reuse the richer previews of the children — and share their
      // key space, so a target both owned and referenced only shows once, as
      // a child. Every other kind renders through the generic
      // ReferenceInline row.
      for (const target of referenceTargets.value ?? []) {
        // A self-reference (the event, or its allocation task) is already
        // the card itself.
        if (target.kind === "task" && target.id === task.value?.id) continue;
        if (
          target.kind === "reference" &&
          target.target.kind === "event" &&
          target.target.id === event.value.id
        )
          continue;
        if (!push(target)) return targets;
      }
      return targets;
    });
    const children = visibleChildTargets;
    const draggedEventTime = computed((): string =>
      draggedEvent.value
        ? locale.value.formatDaytime(getDaytime(draggedEvent.value.start))
        : ""
    );
    const eventEndsInTheFuture = computed(() => event.value.end > clock.value);
    const past = computed<boolean>(() => event.value.end <= clock.value);
    // Allocation
    const isTaskCompleted = computed<boolean>(() => !!task.value?.completed);

    // Description
    const hasDescription = computed<boolean>(() => {
      if (task.value)
        return !!task.value.notes && task.value.notes.blocks.length > 0;
      else return !!event.value.notes && event.value.notes.blocks.length > 0;
    });

    // Labels
    const eventLabels = computed<Array<{ title: string; theme: string }>>(
      () => {
        const record = eventGraphData.value
          ? asGraphRecordResult(eventGraphData.value)
          : undefined;
        return labelsAsTags(browserStore, record?.Labels);
      }
    );

    // Parent
    const clickParent = async (mouseEvent?: MouseEvent) => {
      if (task.value?.parent) {
        await clickHierarchy(controller, router, task.value.parent, mouseEvent);
      }
    };

    // Meeting
    const meeting = computed<{ icon: string; name: string } | null>(() =>
      conference(event.value)
    );
    const attendees = computed<number>(() => totalAttendees(event.value));
    const openUrl = () =>
      eventActions.openConference.action(
        { type: "event", value: event.value },
        { controller }
      );
    // Duration
    const displayedDuration = computed<string>(() =>
      formatCompactDuration(
        resizableDuration.value
          ? resizableDuration.value
          : intervalToDuration({
              start: event.value.start,
              end: event.value.end,
            })
      )
    );

    const openEvent = (mouseEvent?: MouseEvent) => {
      navigateToEvent(router, controller, event.value, mouseEvent);
    };

    const onCheckboxClick = () => {
      if (!task.value) return;
      controller.task.update(
        task.value.id,
        {
          completed: isTaskCompleted.value ? null : new Date(),
        },
        "app",
        null // No reschedule, no modal.
      );
    };
    const showContextMenu = async (e: MouseEvent) => {
      select();
      if (task.value) {
        contextMenu.value.show(
          { type: "allocation", value: { ...task.value, event: event.value } },
          e
        );
      } else {
        contextMenu.value.show(
          {
            type: "event",
            value: {
              ...event.value,
              isCancellable: await isCancellable(event, controller),
              isDeletable: !!calendar.value?.isWritable,
            },
          },
          e
        );
      }
    };
    const select = () => emit("select", event.value.id);
    const unselect = () => (isSelected.value ? emit("unselect") : undefined);
    const isEscapeActive = computed<boolean>(
      () => isSelected.value && !contextMenu.value.isDisplayed
    );
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      return isSelected.value
        ? {
            ...assignShortcuts(
              injection,
              unSelectItem,
              unselect,
              isEscapeActive
            ),
            ...getEventShortcuts(
              { event, task, isWritable, calendar },
              {
                ...injection,
                controller,
                logger: baseLogger,
                profile,
                router,
                simpleModal,
                multipleEventsModal,
                menu,
                palette,
              },
              { closeContextMenu: contextMenu.value.hide }
            ),
          }
        : {};
    });
    const isBrokenIntegration = computed(
      () => task.value?.integrationStatus?.status === "broken"
    );
    const isDeclinedByEverybodyElse = computed(() => {
      if (!event.value.attendees) return false;
      if (!event.value.self) return false;
      const participants = event.value.attendees.filter(
        (a) => a.email !== event.value.self!.email
      );
      if (participants.length === 0) return false;
      return !!participants.every((a) => a.status === "no");
    });
    return {
      attendees,
      children,
      clickParent,
      compact,
      displayedDuration,
      displayLocationLine,
      draggedEventTime,
      duration,
      eventColorStyle,
      eventEndsInTheFuture,
      eventLabels,
      getDaytime,
      hasDescription,
      isBrokenIntegration,
      isTaskCompleted,
      locale,
      meeting,
      onCheckboxClick,
      ongoing,
      openEvent,
      openUrl,
      parentTitle,
      past,
      select,
      shortcuts,
      showContextMenu,
      small,
      style,
      taskCheckboxTooltip,
      task,
      unselect,
      isDeclinedByEverybodyElse,
    };
  },
});
</script>

<style scoped lang="scss">
.event {
  height: 100%;
  width: calc(100% - 12px);
  padding: 6px 0 12px 0;
  display: flex;
  position: relative;
  box-sizing: border-box;
  flex-direction: column;
  row-gap: 6px;
  border-radius: $border-radius-m;
  cursor: default;
  @include transition(background 100ms ease-out);
  @include themed() {
    background: t($bg-event);
    border: 1px solid t($grey-15);
  }
  &.short {
    padding-top: 0;
    padding-bottom: 0;
    row-gap: 0;
    &.ongoing {
      padding-bottom: 1px;
    }
    .line {
      height: 100%;
      align-items: center;
    }
  }
  &.small {
    .progress-time {
      position: absolute;
      inset-inline-start: 4px;
      bottom: calc(0% - 12px);
    }
    &.compact .progress-time {
      inset-inline-start: 4px;
      bottom: calc(0% - 9px);
    }
  }
  &.compact {
    row-gap: 2px;
  }
  &.hasChildren {
    padding-bottom: 0;

    .line.second {
      margin-bottom: 0;
    }
  }
  .children {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    row-gap: 1px;
    padding-block: 0;
    padding-inline-end: 16px;
    padding-inline-start: 14px;
    margin: -4px 0 0;
    overflow: hidden;
    list-style: none;
  }
  .child {
    min-width: 0;
    height: 17px;
  }
  &.past {
    @include themed() {
      background-color: t($bg-past-event);
      border: 1px solid t($grey-15);
    }
    .calendar-border {
      opacity: 0.3;
    }
    .line .content-line {
      .left-details {
        .check:not(.checked) {
          @include themed() {
            background-color: t($grey-03);
          }
        }

        .event-title {
          @include themed() {
            color: t($title-past-event);
          }
        }
      }
      .right-details {
        .compact-attendees {
          opacity: 0.5;
        }
      }
    }
    .context-button {
      opacity: 0.6;
    }
  }
  &.ongoing .line .content-line {
    .left-details .event-title {
      font-weight: 500;
    }
    .right-details .event-duration {
      @include themed() {
        color: t($grey-70);
      }
    }
  }

  :deep(.warning-icon) path.fill {
    @include themed() {
      fill: t($secondary-50);
    }
  }

  .warning-icon {
    height: 14px;

    &.mini {
      width: 12px;
      height: 12px;
      padding-top: 2px;
    }
  }

  .dragged-start {
    position: absolute;
    inset-inline-end: calc(100% + 14px);
    z-index: $z-index-interactive-high;
    padding: 3px 6px;
    @include themed() {
      background-color: t($bg);
      color: t($grey-40);
    }
  }

  .line {
    padding: 0 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    &.second {
      margin-bottom: 12px;
      column-gap: 16px;
    }
    .base-location {
      margin-inline-start: -6px;
    }
    .calendar-border {
      width: 3px;
      height: 20px;
      border-radius: 10px;
      margin-inline-start: -17px;
    }
    .content-line {
      margin-inline-start: 12px;
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      column-gap: 10px;
      min-width: 0;
      .left-details {
        display: flex;
        flex-direction: row;
        column-gap: 10px;
        align-items: center;
        min-width: 0;
        .task-label {
          height: 16px;
        }
        .event-title {
          flex-shrink: 1;
          @extend %font-body-regular;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          @include themed() {
            color: t($primary);
          }
        }
        .description {
          min-width: 16px;
          height: 15px;
        }
        .recording-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex: 0 0 auto;
          animation: recording-pulse 2s ease-in-out infinite;
          @include themed() {
            background: t($secondary-50);
            box-shadow: 0 0 0 3px rgba(t($secondary-50), 0.18);
          }
        }
        .hierarchy {
          flex-shrink: 6;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          min-width: 50px;
        }
        .event-tags {
          display: flex;
          flex-shrink: 1;
          align-items: center;
          min-width: 0;
          overflow: hidden;

          :deep(.rtag) {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          :deep(.rtag:not(:first-child)) {
            transition:
              max-width 150ms ease-out,
              margin-inline-start 150ms ease-out;
          }

          :deep(.rtag:first-child) {
            margin-inline-start: 4px;
            max-width: 120px;
          }

          :deep(.rtag:first-child:not(:only-child)) {
            order: 1;
          }

          :deep(.rtag:not(:first-child)) {
            max-width: 14px;
            overflow: hidden;
            margin-inline-start: 0px;
          }

          &:hover :deep(.rtag:not(:first-child)) {
            max-width: 140px;
            margin-inline-start: 2px;
            text-overflow: ellipsis;
          }
        }
      }
      .right-details {
        display: flex;
        flex-direction: row;
        column-gap: 10px;
        align-items: center;
        .event-duration {
          @include themed() {
            color: t($grey-40);
          }
        }
      }
    }
  }
  &:not(.draggingOver).isSelected {
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.05);
    @include themed() {
      border-color: t($grey-50);
    }
  }
  &:not(.draggingOver):not(.isSelected):hover {
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03);
    @include themed() {
      border-color: t($grey-30);
    }
  }

  &.calendar-a--light,
  &.calendar-a--dark {
    .calendar-border {
      background-color: $calendar-a-50;
    }
  }

  &.calendar-b--light,
  &.calendar-b--dark {
    .calendar-border {
      background-color: $calendar-b-50;
    }
  }

  &.calendar-c--light,
  &.calendar-c--dark {
    .calendar-border {
      background-color: $calendar-c-50;
    }
  }

  &.calendar-d--light,
  &.calendar-d--dark {
    .calendar-border {
      background-color: $calendar-d-50;
    }
  }

  &.calendar-e--light,
  &.calendar-e--dark {
    .calendar-border {
      background-color: $calendar-e-50;
    }
  }

  &.calendar-f--light,
  &.calendar-f--dark {
    .calendar-border {
      background-color: $calendar-f-50;
    }
  }

  &.calendar-g--light,
  &.calendar-g--dark {
    .calendar-border {
      background-color: $calendar-g-50;
    }
  }

  &.calendar-h--light,
  &.calendar-h--dark {
    .calendar-border {
      background-color: $calendar-h-50;
    }
  }

  &.calendar-i--light,
  &.calendar-i--dark {
    .calendar-border {
      background-color: $calendar-i-50;
    }
  }
}

@keyframes recording-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.3;
    transform: scale(0.88);
  }
}
</style>
